import { z } from 'zod';

// ============================================================================
// PAYMENT SERVICE VALIDATION SCHEMAS
// ============================================================================

// Checkout Session Schemas
export const createCheckoutSessionRequestSchema = z.object({
  priceId: z.string().min(1, 'Price ID is required'),
});

export const createCheckoutSessionResponseSchema = z.object({
  url: z.url('Checkout URL must be a valid URL'),
});

export const sessionStatusResponseSchema = z.object({
  status: z.string().min(1, 'Status is required'),
  customer_email: z.email().nullable(),
});

// Subscription Schemas
// Stripe subscription status values as per Stripe API documentation
export const subscriptionStatusEnum = z.enum(
  [
    'active',
    'past_due',
    'canceled',
    'trialing',
    'incomplete',
    'incomplete_expired',
    'unpaid',
    'paused',
  ],
  {
    error: () => ({
      message:
        'Status must be one of: active, past_due, canceled, trialing, incomplete, incomplete_expired, unpaid, paused',
    }),
  },
);

export type SubscriptionStatus = z.infer<typeof subscriptionStatusEnum>;

export const subscriptionDetailsSchema = z.object({
  subscription_id: z.string().min(1, 'Subscription ID is required'),
  status: subscriptionStatusEnum,
  plan_name: z.string().min(1, 'Plan name is required'),
  plan_id: z.string().min(1, 'Plan ID is required'),
  price_id: z.string().min(1, 'Price ID is required'),
  amount: z.number().int().nonnegative('Amount must be non-negative'),
  currency: z.string().length(3, 'Currency must be a 3-letter code'),
  interval: z.enum(['month', 'year'], {
    error: () => ({ message: 'Interval must be "month" or "year"' }),
  }),
  current_period_start: z.number().int().nonnegative('Period start must be a valid Unix timestamp'),
  current_period_end: z.number().int().nonnegative('Period end must be a valid Unix timestamp'),
  cancel_at_period_end: z.boolean(),
  canceled_at: z.number().int().nonnegative().nullable(),
  trial_end: z.number().int().nonnegative().nullable(),
  all_price_ids: z.array(z.string().min(1)).optional(),
  total_amount: z.number().int().nonnegative().optional(),
});

export const updateSubscriptionRequestSchema = z.object({
  new_price_id: z.string().min(1, 'New price ID is required'),
});

export const paymentMessageResponseSchema = z.object({
  success: z.boolean(),
  message: z.string().min(1, 'Message is required'),
});

// Payment Method Schemas
export const paymentMethodDetailsSchema = z.object({
  id: z.string().min(1, 'Payment method ID is required'),
  type: z.string().min(1, 'Payment method type is required'),
  card_brand: z.string().nullable(),
  card_last4: z.string().nullable(),
  card_exp_month: z.number().int().min(1).max(12).nullable(),
  card_exp_year: z.number().int().min(2020).nullable(),
  billing_email: z.string().email().nullable(),
  is_default: z.boolean(),
});

export const paymentMethodDetailsArraySchema = z.array(paymentMethodDetailsSchema);

export const setupIntentResponseSchema = z.object({
  client_secret: z.string().min(1, 'Client secret is required'),
});

export const attachPaymentMethodRequestSchema = z.object({
  payment_method_id: z.string().min(1, 'Payment method ID is required'),
});

// Invoice Schemas
export const invoiceLineItemSchema = z.object({
  description: z.string().min(1, 'Description is required'),
  amount: z.number().int(), // Can be negative for credits, refunds, discounts, etc.
  currency: z.string().length(3, 'Currency must be a 3-letter code'),
  quantity: z.number().int().positive('Quantity must be positive'),
});

export const invoiceSchema = z.object({
  id: z.string().min(1, 'Invoice ID is required'),
  invoice_number: z.string().nullable(),
  status: z.enum(['draft', 'open', 'paid', 'void', 'uncollectible'], {
    error: () => ({ message: 'Status must be one of: draft, open, paid, void, uncollectible' }),
  }),
  amount_due: z.number().int().nonnegative('Amount due must be non-negative'),
  amount_paid: z.number().int().nonnegative('Amount paid must be non-negative'),
  currency: z.string().length(3, 'Currency must be a 3-letter code'),
  created: z.number().int().nonnegative('Created timestamp must be valid'),
  due_date: z.number().int().nonnegative().nullable(),
  paid_at: z.number().int().nonnegative().nullable(),
  payment_method_brand: z.string().nullable(),
  payment_method_last4: z.string().nullable(),
  customer_email: z.email().nullable(),
  invoice_pdf: z.url().nullable(),
  hosted_invoice_url: z.url().nullable(),
  line_items: z.array(invoiceLineItemSchema),
});

export const invoiceListResponseSchema = z.object({
  invoices: z.array(invoiceSchema),
  has_more: z.boolean(),
  total_count: z.number().int().nonnegative('Total count must be non-negative').nullable(),
});

export const upcomingInvoiceSchema = z.object({
  amount_due: z.number().int().nonnegative('Amount due must be non-negative'),
  currency: z.string().length(3, 'Currency must be a 3-letter code'),
  period_start: z.number().int().nonnegative('Period start must be a valid Unix timestamp'),
  period_end: z.number().int().nonnegative('Period end must be a valid Unix timestamp'),
  next_payment_attempt: z.number().int().nonnegative().nullable(),
  line_items: z.array(invoiceLineItemSchema),
});

export const invoiceListParamsSchema = z.object({
  limit: z.number().int().positive().optional(),
  starting_after: z.string().optional(),
  created_gte: z.number().int().nonnegative().optional(),
  created_lte: z.number().int().nonnegative().optional(),
});

export const portalSessionResponseSchema = z.object({
  url: z.url('Portal URL must be a valid URL'),
});

// PDF Response Schema (for invoice PDF endpoint)
export const invoicePdfResponseSchema = z.object({
  pdf_url: z.url('PDF URL must be a valid URL'),
});

// Price Schemas
export const priceInfoSchema = z.object({
  id: z.string().min(1, 'Price ID is required'),
  unit_amount: z.number().int().nullable(),
  currency: z.string().length(3, 'Currency must be a 3-letter code'),
  interval: z.enum(['month', 'year']).nullable(),
  nickname: z.string().nullable(),
  product_id: z.string().min(1, 'Product ID is required'),
});

export const pricesResponseSchema = z.object({
  prices: z.array(priceInfoSchema),
});

// Type exports for Payment Service
export type CreateCheckoutSessionRequest = z.infer<typeof createCheckoutSessionRequestSchema>;
export type CreateCheckoutSessionResponse = z.infer<typeof createCheckoutSessionResponseSchema>;
export type SessionStatusResponse = z.infer<typeof sessionStatusResponseSchema>;
export type SubscriptionDetails = z.infer<typeof subscriptionDetailsSchema>;
export type UpdateSubscriptionRequest = z.infer<typeof updateSubscriptionRequestSchema>;
export type PaymentMessageResponse = z.infer<typeof paymentMessageResponseSchema>;
export type PaymentMethodDetails = z.infer<typeof paymentMethodDetailsSchema>;
export type SetupIntentResponse = z.infer<typeof setupIntentResponseSchema>;
export type AttachPaymentMethodRequest = z.infer<typeof attachPaymentMethodRequestSchema>;
export type InvoiceLineItem = z.infer<typeof invoiceLineItemSchema>;
export type Invoice = z.infer<typeof invoiceSchema>;
export type InvoiceListResponse = z.infer<typeof invoiceListResponseSchema>;
export type UpcomingInvoice = z.infer<typeof upcomingInvoiceSchema>;
export type InvoiceListParams = z.infer<typeof invoiceListParamsSchema>;
export type PortalSessionResponse = z.infer<typeof portalSessionResponseSchema>;
export type InvoicePdfResponse = z.infer<typeof invoicePdfResponseSchema>;
export type PriceInfo = z.infer<typeof priceInfoSchema>;
export type PricesResponse = z.infer<typeof pricesResponseSchema>;

// Validation helper functions for Payment Service
export const validateCreateCheckoutSessionRequest = (data: unknown) => {
  return createCheckoutSessionRequestSchema.safeParse(data);
};

export const validateCreateCheckoutSessionResponse = (data: unknown) => {
  return createCheckoutSessionResponseSchema.safeParse(data);
};

export const validateSessionStatusResponse = (data: unknown) => {
  return sessionStatusResponseSchema.safeParse(data);
};

export const validateSubscriptionDetails = (data: unknown) => {
  return subscriptionDetailsSchema.safeParse(data);
};

export const validateUpdateSubscriptionRequest = (data: unknown) => {
  return updateSubscriptionRequestSchema.safeParse(data);
};

export const validatePaymentMessageResponse = (data: unknown) => {
  return paymentMessageResponseSchema.safeParse(data);
};

export const validatePaymentMethodDetails = (data: unknown) => {
  return paymentMethodDetailsSchema.safeParse(data);
};

export const validateSetupIntentResponse = (data: unknown) => {
  return setupIntentResponseSchema.safeParse(data);
};

export const validateAttachPaymentMethodRequest = (data: unknown) => {
  return attachPaymentMethodRequestSchema.safeParse(data);
};

export const validateInvoice = (data: unknown) => {
  return invoiceSchema.safeParse(data);
};

export const validateInvoiceListResponse = (data: unknown) => {
  return invoiceListResponseSchema.safeParse(data);
};

export const validateUpcomingInvoice = (data: unknown) => {
  return upcomingInvoiceSchema.safeParse(data);
};

export const validateInvoiceListParams = (data: unknown) => {
  return invoiceListParamsSchema.safeParse(data);
};

export const validatePortalSessionResponse = (data: unknown) => {
  return portalSessionResponseSchema.safeParse(data);
};

export const validateInvoicePdfResponse = (data: unknown) => {
  return invoicePdfResponseSchema.safeParse(data);
};

export const validatePricesResponse = (data: unknown) => {
  return pricesResponseSchema.safeParse(data);
};
